import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { adminAPI } from "../../services/api";
import { User } from "../../types";
import {
  MdVerifiedUser,
  MdSearch,
  MdCheckCircle,
  MdCancel,
  MdRefresh,
  MdWarning,
  MdPhone,
  MdLocationOn,
  MdFlag,
  MdImage,
  MdDownload,
  MdVisibility,
} from "react-icons/md";
import countries from "../../arrays/countries";
import getDocumentUrl from "../../utils/getDocumentUrl";

const KYCManagement = () => {
  const { addAlert } = useAlert();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const VerifiedIcon = MdVerifiedUser as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const SearchIcon = MdSearch as React.FC<React.SVGProps<SVGSVGElement>>;
  const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;
  const CancelIcon = MdCancel as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const WarningIcon = MdWarning as React.FC<React.SVGProps<SVGSVGElement>>;
  const PhoneIcon = MdPhone as React.FC<React.SVGProps<SVGSVGElement>>;
  const LocationIcon = MdLocationOn as React.FC<React.SVGProps<SVGSVGElement>>;
  const FlagIcon = MdFlag as React.FC<React.SVGProps<SVGSVGElement>>;
  const ImageIcon = MdImage as React.FC<React.SVGProps<SVGSVGElement>>;
  const DownloadIcon = MdDownload as React.FC<React.SVGProps<SVGSVGElement>>;
  const VisibilityIcon = MdVisibility as React.FC<
    React.SVGProps<SVGSVGElement>
  >;

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch users" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.username?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((user) => {
        if (statusFilter === "has_data") {
          return user.kyc_data !== null && user.kyc_data !== undefined;
        }
        return user.kyc_status === statusFilter;
      });
    }

    setFilteredUsers(filtered);
  }, [searchTerm, statusFilter, users]);

  const handleApproveKYC = async (userId: number) => {
    try {
      await adminAPI.approveKYC({ user_id: userId });
      addAlert({ type: "success", message: "KYC approved successfully" });
      fetchUsers();
      setIsDetailModalOpen(false);
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to approve KYC",
      });
    }
  };

  const handleRejectKYC = async (userId: number) => {
    const reason = prompt("Please enter the reason for rejection:");
    if (!reason?.trim()) {
      addAlert({ type: "error", message: "Rejection reason is required" });
      return;
    }

    try {
      await adminAPI.rejectKYC({ user_id: userId, reason });
      addAlert({ type: "success", message: "KYC rejected successfully" });
      fetchUsers();
      setIsDetailModalOpen(false);
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to reject KYC",
      });
    }
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setIsDetailModalOpen(true);
  };

  const handleDownloadDocument = async (user: User) => {
    if (user.kyc_data?.identity_document_path) {
      try {
        const documentUrl = getDocumentUrl(
          user.kyc_data.identity_document_path
        );
        if (!documentUrl) {
          addAlert({ type: "error", message: "Invalid document path" });
          return;
        }

        const response = await fetch(documentUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch document");
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const fileExtension = user.kyc_data.identity_document_path
          .split(".")
          .pop();
        link.download = `kyc_document_${user.id}_${user.first_name}_${
          user.last_name
        }.${fileExtension || "jpg"}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        addAlert({ type: "success", message: "Document download started" });
      } catch (error) {
        console.error("Download error:", error);
        addAlert({
          type: "error",
          message:
            "Failed to download document. The file may not be accessible.",
        });
      }
    } else {
      addAlert({
        type: "error",
        message: "No document available for download",
      });
    }
  };

  const handleViewDocument = (user: User) => {
    if (user.kyc_data?.identity_document_path) {
      const documentUrl = getDocumentUrl(user.kyc_data.identity_document_path);
      if (documentUrl) {
        window.open(documentUrl, "_blank", "noopener,noreferrer");
      } else {
        addAlert({ type: "error", message: "Invalid document URL" });
      }
    } else {
      addAlert({
        type: "error",
        message: "No document available to view",
      });
    }
  };

  const getKYCStatusBadge = (user: User) => {
    if (user.kyc_status === "pending" && !user.kyc_data) {
      return (
        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full dark:bg-gray-900 dark:text-gray-200">
          Not Submitted
        </span>
      );
    }

    switch (user.kyc_status) {
      case "verified":
        return (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full dark:bg-green-900 dark:text-green-200">
            Verified
          </span>
        );
      case "pending":
        return (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full dark:bg-yellow-900 dark:text-yellow-200">
            Pending Review
          </span>
        );
      case "rejected":
        return (
          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full dark:bg-red-900 dark:text-red-200">
            Rejected
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full dark:bg-gray-900 dark:text-gray-200">
            Not Submitted
          </span>
        );
    }
  };

  const getAccountLevelBadge = (level: string) => {
    switch (level) {
      case "3":
        return (
          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full dark:bg-purple-900 dark:text-purple-200">
            Level 3
          </span>
        );
      case "2":
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full dark:bg-blue-900 dark:text-blue-200">
            Level 2
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full dark:bg-gray-900 dark:text-gray-200">
            Level 1
          </span>
        );
    }
  };

  const getCountryName = (countryCode: string) => {
    const country = countries.find((c) => c.code === countryCode);
    return country ? country.name : countryCode;
  };

  const getDocumentType = (filePath: string) => {
    if (filePath.includes(".pdf")) return "PDF Document";
    if (filePath.includes(".png")) return "PNG Image";
    if (filePath.includes(".jpg") || filePath.includes(".jpeg"))
      return "JPEG Image";
    return "Document";
  };

  const pendingKYCUsers = users.filter(
    (user) => user.kyc_status === "pending" && user.kyc_data !== null
  );

  const usersWithKYCData = users.filter((user) => user.kyc_data !== null);

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            KYC Management
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Review and manage user KYC verification requests and documents.
          </p>
        </div>
        <button
          onClick={fetchUsers}
          disabled={isLoading}
          className={`${GlobalStyles.buttonIconOutline} ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Stats and Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <div className={GlobalStyles.inputWrapper}>
            <SearchIcon className={GlobalStyles.inputIcon} />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={GlobalStyles.input}
            />
          </div>
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={GlobalStyles.input}
          >
            <option value="pending">Pending Review</option>
            <option value="has_data">With KYC Data</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="all">All Status</option>
          </select>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-yellow-600`}>
            {pendingKYCUsers.length}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Pending KYC
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-green-600`}>
            {usersWithKYCData.length}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            With KYC Data
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className={GlobalStyles.tableContainer}>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className={`${CommonClasses.textGray} mt-4`}>
              Loading KYC requests...
            </p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <VerifiedIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={`${CommonClasses.textGray} mb-2`}>
              No KYC requests found
            </p>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "No KYC requests to review"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead className={GlobalStyles.tableHeader}>
                <tr>
                  <th className={GlobalStyles.tableHeaderCell}>User</th>
                  <th className={GlobalStyles.tableHeaderCell}>
                    Account Level
                  </th>
                  <th className={GlobalStyles.tableHeaderCell}>KYC Status</th>
                  <th className={GlobalStyles.tableHeaderCell}>Submitted</th>
                  <th className={GlobalStyles.tableHeaderCell}>Actions</th>
                </tr>
              </thead>
              <tbody className={GlobalStyles.tableBody}>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={GlobalStyles.tableRow}>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-3`}>
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {user.first_name?.[0]}
                            {user.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <div
                            className={`font-medium ${CommonClasses.textGrayStrong}`}
                          >
                            {user.first_name} {user.last_name}
                          </div>
                          <div
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                          >
                            {user.email}
                          </div>
                          <div
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                          >
                            @{user.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      {getAccountLevelBadge(user.account_level)}
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      {getKYCStatusBadge(user)}
                      {user.kyc_data && (
                        <div
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                        >
                          Has KYC Data
                        </div>
                      )}
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div
                        className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong}`}
                      >
                        {user.kyc_data?.submitted_at
                          ? new Date(
                              user.kyc_data.submitted_at
                            ).toLocaleDateString()
                          : "Not submitted"}
                      </div>
                      <div
                        className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                      >
                        {user.kyc_data?.submitted_at
                          ? new Date(
                              user.kyc_data.submitted_at
                            ).toLocaleTimeString()
                          : "-"}
                      </div>
                    </td>
                    <td className={GlobalStyles.tableCell}>
                      <div className={`${CommonClasses.flexStart} gap-2`}>
                        {user.kyc_data && (
                          <>
                            <button
                              onClick={() => handleViewDetails(user)}
                              className={`${GlobalStyles.buttonPrimary} !text-xs`}
                              title="View KYC Details"
                            >
                              <VisibilityIcon className="w-3 h-3 mr-1" />
                              View
                            </button>
                            <button
                              onClick={() => handleDownloadDocument(user)}
                              className={`${GlobalStyles.buttonIconOutline}`}
                              title="Download Document"
                            >
                              <DownloadIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {user.kyc_status === "pending" && user.kyc_data && (
                          <>
                            <button
                              onClick={() => handleApproveKYC(user.id)}
                              className={`${GlobalStyles.buttonIconPrimary}`}
                              title="Approve KYC"
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRejectKYC(user.id)}
                              className={`${GlobalStyles.buttonIconDanger}`}
                              title="Reject KYC"
                            >
                              <CancelIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {!user.kyc_data && (
                          <span
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                          >
                            No data
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* KYC Details Modal */}
      {isDetailModalOpen && selectedUser && selectedUser.kyc_data && (
        <div className={GlobalStyles.modalOverlay}>
          <div className={GlobalStyles.modalContainer}>
            <div
              className={`p-5 border-b border-gray-300 dark:border-gray-700`}
            >
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
              >
                KYC Verification Details
              </h2>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-1`}
              >
                {selectedUser.first_name} {selectedUser.last_name} -{" "}
                {selectedUser.email}
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* User Information */}
              <div>
                <h3
                  className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
                >
                  User Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={GlobalStyles.label}>Full Name</label>
                    <div className={`${GlobalStyles.sectionWrap}`}>
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedUser.first_name} {selectedUser.last_name}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={GlobalStyles.label}>Email</label>
                    <div className={`${GlobalStyles.sectionWrap}`}>
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedUser.email}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={GlobalStyles.label}>Username</label>
                    <div className={`${GlobalStyles.sectionWrap}`}>
                      <p className={CommonClasses.textGrayStrong}>
                        @{selectedUser.username}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={GlobalStyles.label}>Account Level</label>
                    <div className={`${GlobalStyles.sectionWrap}`}>
                      {getAccountLevelBadge(selectedUser.account_level)}
                    </div>
                  </div>
                </div>
              </div>

              {/* KYC Information */}
              <div>
                <h3
                  className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
                >
                  KYC Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={GlobalStyles.label}>Phone Number</label>
                    <div
                      className={`${GlobalStyles.sectionWrap} ${CommonClasses.flexStart} gap-2`}
                    >
                      <PhoneIcon className="w-4 h-4 text-gray-500" />
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedUser.kyc_data.phone_number || "Not provided"}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={GlobalStyles.label}>Country</label>
                    <div
                      className={`${GlobalStyles.sectionWrap} ${CommonClasses.flexStart} gap-2`}
                    >
                      <FlagIcon className="w-4 h-4 text-gray-500" />
                      <p className={CommonClasses.textGrayStrong}>
                        {getCountryName(selectedUser.kyc_data.country) ||
                          "Not provided"}
                      </p>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className={GlobalStyles.label}>Address</label>
                    <div
                      className={`${GlobalStyles.sectionWrap} ${CommonClasses.flexStart} gap-2`}
                    >
                      <LocationIcon className="w-4 h-4 text-gray-500 mt-0.5" />
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedUser.kyc_data.address || "Not provided"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Identity Document */}
              <div>
                <h3
                  className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
                >
                  Identity Document
                </h3>
                {selectedUser.kyc_data.identity_document_path ? (
                  <div className="space-y-4">
                    <div
                      className={`${CommonClasses.flexBetween} p-4 bg-gray-50 dark:bg-gray-700 rounded-lg max-sm:flex-col max-sm:gap-2`}
                    >
                      <div className={`${CommonClasses.flexStart} gap-3`}>
                        <ImageIcon className="w-8 h-8 text-gray-500" />
                        <div>
                          <p
                            className={`font-medium ${CommonClasses.textGrayStrong}`}
                          >
                            Identity Document
                          </p>
                          <p
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                          >
                            {getDocumentType(
                              selectedUser.kyc_data.identity_document_path
                            )}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`${CommonClasses.flexStart} gap-2 max-sm:w-full max-sm:justify-start`}
                      >
                        <button
                          onClick={() => handleViewDocument(selectedUser)}
                          className={`${GlobalStyles.buttonPrimary} !text-sm`}
                        >
                          <VisibilityIcon className="w-4 h-4 mr-2" />
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(selectedUser)}
                          className={`${GlobalStyles.buttonSecondary} !text-sm`}
                        >
                          <DownloadIcon className="w-4 h-4 mr-2" />
                          Download
                        </button>
                      </div>
                    </div>

                    {/* Document Preview */}
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                      {selectedUser.kyc_data.identity_document_path.includes(
                        ".pdf"
                      ) ? (
                        <div className="text-center p-8">
                          <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <p className={CommonClasses.textGray}>
                            PDF Document - Use download or view button
                          </p>
                          <p
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-2`}
                          >
                            Click "View" to open in new tab or "Download" to
                            save the file
                          </p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <img
                            src={
                              getDocumentUrl(
                                selectedUser.kyc_data.identity_document_path
                              ) || ""
                            }
                            alt="Identity Document"
                            className="w-full h-auto max-h-64 object-contain rounded mx-auto"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                              const errorDiv =
                                target.nextElementSibling as HTMLElement;
                              if (errorDiv) {
                                errorDiv.style.display = "block";
                              }
                            }}
                          />
                          <div className="hidden text-center p-8">
                            <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className={CommonClasses.textGray}>
                              Document preview not available
                            </p>
                            <p
                              className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-2`}
                            >
                              Use the download or view button to access the
                              document
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                    <WarningIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className={CommonClasses.textGray}>
                      No identity document uploaded
                    </p>
                  </div>
                )}
              </div>

              {/* Submission Details */}
              <div>
                <h3
                  className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
                >
                  Submission Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={GlobalStyles.label}>
                      Submission Date
                    </label>
                    <div className={`${GlobalStyles.sectionWrap}`}>
                      <p className={CommonClasses.textGrayStrong}>
                        {selectedUser.kyc_data.submitted_at
                          ? new Date(
                              selectedUser.kyc_data.submitted_at
                            ).toLocaleString()
                          : "Not available"}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={GlobalStyles.label}>Current Status</label>
                    <div className={`${GlobalStyles.sectionWrap}`}>
                      {getKYCStatusBadge(selectedUser)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {selectedUser.kyc_status === "pending" && (
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 max-sm:flex-col">
                  <button
                    onClick={() => handleApproveKYC(selectedUser.id)}
                    className={`${GlobalStyles.buttonPrimary} flex-1`}
                  >
                    <CheckIcon className="w-5 h-5 mr-2" />
                    Approve KYC
                  </button>
                  <button
                    onClick={() => handleRejectKYC(selectedUser.id)}
                    className={`${GlobalStyles.buttonOutline} flex-1`}
                  >
                    <CancelIcon className="w-5 h-5 mr-2" />
                    Reject KYC
                  </button>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-gray-300 dark:border-gray-700">
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className={`${GlobalStyles.buttonPrimary} w-full`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCManagement;
